import { PlusIcon } from '@radix-ui/react-icons';
import { useEffect, useId, useState } from 'react';
import { Button } from '../../../../primitives/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../primitives/popover';
import { ToyBrickIcon } from '../../../../svg/toy-brick-icon';
import { BLOCK_ICONS } from '../constants/block-icons';

const BLOCK_MENU_OPEN_EVENT = 'editor-email:block-menu-open';

const BLOCK_TYPE_OPTIONS = [
  { id: 'header', value: 'header', label: 'Encabezado', icon: BLOCK_ICONS.Heading },
  { id: 'text', value: 'text', label: 'Texto', icon: BLOCK_ICONS.Text },
  { id: 'button', value: 'button', label: 'Botón', icon: BLOCK_ICONS.Button },
  { id: 'image', value: 'image', label: 'Imagen', icon: BLOCK_ICONS.Image },
  { id: 'divider', value: 'divider', label: 'Divisor', icon: BLOCK_ICONS.Divider },
  { id: 'html', value: 'html', label: 'HTML', icon: BLOCK_ICONS.Html },
  { id: 'avatar', value: 'avatar', label: 'Avatar', icon: BLOCK_ICONS.Avatar },
  { id: 'container', value: 'container', label: 'Contenedor', icon: BLOCK_ICONS.Container },
  { id: 'column', value: 'column', label: 'Columnas', icon: BLOCK_ICONS.ColumnsContainer },
  { id: 'space', value: 'space', label: 'Espacio', icon: BLOCK_ICONS.Spacer },
  { id: 'block', value: 'block', label: 'Bloque', icon: ToyBrickIcon },
] as const;

export type BlockTypeOption = (typeof BLOCK_TYPE_OPTIONS)[number]['value'];

type BlockMenuProps = {
  position: 'top' | 'bottom';
  onSelectBlockType: (blockType: BlockTypeOption) => void;
};

export function BlockMenu({ position, onSelectBlockType }: BlockMenuProps) {
  const menuId = useId();
  const [open, setOpen] = useState(false);

  const positionLabel = position === 'top' ? 'arriba' : 'abajo';

  useEffect(() => {
    const handleMenuOpen = (event: Event) => {
      const customEvent = event as CustomEvent<{ menuId: string }>;
      if (customEvent.detail?.menuId !== menuId) {
        setOpen(false);
      }
    };

    window.addEventListener(BLOCK_MENU_OPEN_EVENT, handleMenuOpen);

    return () => {
      window.removeEventListener(BLOCK_MENU_OPEN_EVENT, handleMenuOpen);
    };
  }, [menuId]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      window.dispatchEvent(
        new CustomEvent(BLOCK_MENU_OPEN_EVENT, {
          detail: { menuId },
        }),
      );
      setOpen(true);
      return;
    }

    setOpen(nextOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          aria-label={`Agregar elemento ${positionLabel}`}
          className="size-7"
          data-block-menu-trigger
          size="icon-sm"
          title={`Agregar elemento ${positionLabel}`}
          variant="accent"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <PlusIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        className="w-[22rem] p-2 shadow-lg"
        data-block-menu-content
        onCloseAutoFocus={(event) => {
          // Avoid focus returning to another trigger when menus close each other.
          event.preventDefault();
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="flex flex-wrap gap-2">
          {BLOCK_TYPE_OPTIONS.map((option) => (
            <Button
              key={option.id}
              variant="outline"
              size="xs"
              onClick={(event) => {
                event.stopPropagation();
                onSelectBlockType(option.value);
                setOpen(false);
              }}
            >
              <option.icon />
              <span className="leading-tight">{option.label}</span>
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
